import React from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EnhancementTogglesPanel } from "@/components/shared/EnhancementTogglesPanel";
import { SitelinksSection } from "@/components/shared/SitelinksSection";
import type { SitelinkItem } from "@/components/shared/SitelinksSection";
import { useEnhancementConfig } from "@/hooks/useEnhancementConfig";
import type { AdAccountSettings } from "@/hooks/useOnboardingSettings";

interface StepEnhancementsProps {
  adAccountId: string;
  settings: AdAccountSettings | null;
  onSave: (data: Partial<AdAccountSettings>) => Promise<any>;
  onValidChange: (valid: boolean) => void;
}

type EnhancementMap = Record<string, boolean>;

// Storage columns keyed by format_key
const FORMAT_TO_SETTINGS_KEY: Record<string, string> = {
  single_image: "image_enhancements",
  single_video: "video_enhancements",
  carousel: "carousel_enhancements",
  collection: "collection_enhancements",
  catalog: "enhancements_catalog",
  flexible: "flexible_advantage_plus_creative",
};

function settingsToValues(settings: AdAccountSettings | null): Record<string, boolean> {
  if (!settings) return {};
  const values: Record<string, boolean> = {};

  // Boolean JSONB maps
  for (const formatKey of ["single_image", "single_video", "carousel", "catalog"]) {
    const settingsKey = FORMAT_TO_SETTINGS_KEY[formatKey];
    const map = (settings as any)?.[settingsKey] as EnhancementMap | null;
    if (map) {
      for (const [k, v] of Object.entries(map)) {
        values[k] = !!v;
      }
    }
  }

  // Collection uses OPT_IN/OPT_OUT strings
  const collectionMap = (settings as any)?.collection_enhancements as Record<string, string> | null;
  if (collectionMap) {
    for (const [k, v] of Object.entries(collectionMap)) {
      values[k] = v === "OPT_IN";
    }
  }

  // Flexible is a single boolean
  if ((settings as any)?.flexible_advantage_plus_creative !== undefined) {
    values["flexible_advantage_plus_creative"] = !!(settings as any).flexible_advantage_plus_creative;
  }

  return values;
}

function valuesToSettings(
  values: Record<string, boolean>,
  toggles: { format_key: string; toggle_key: string }[],
): Partial<AdAccountSettings> {
  const grouped: Record<string, string[]> = {};
  for (const t of toggles) {
    if (!grouped[t.format_key]) grouped[t.format_key] = [];
    grouped[t.format_key].push(t.toggle_key);
  }

  const result: Record<string, any> = {};

  // Boolean JSONB maps
  for (const formatKey of ["single_image", "single_video", "carousel", "catalog"]) {
    const settingsKey = FORMAT_TO_SETTINGS_KEY[formatKey];
    const keys = grouped[formatKey] ?? [];
    result[settingsKey] = Object.fromEntries(keys.map((k) => [k, values[k] === true]));
  }

  // Collection: OPT_IN/OPT_OUT
  const collectionKeys = grouped["collection"] ?? [];
  result["collection_enhancements"] = Object.fromEntries(
    collectionKeys.map((k) => [k, values[k] ? "OPT_IN" : "OPT_OUT"]),
  );

  // Flexible: single boolean
  result["flexible_advantage_plus_creative"] = values["flexible_advantage_plus_creative"] === true;

  return result as Partial<AdAccountSettings>;
}

export function StepEnhancements({
  adAccountId,
  settings,
  onSave,
  onValidChange,
}: StepEnhancementsProps) {
  const { data: config } = useEnhancementConfig();
  const [values, setValues] = useState<Record<string, boolean>>({});
  const [sitelinks, setSitelinks] = useState<SitelinkItem[]>([]);
  const hasLoaded = useRef(false);

  // Sync from loaded settings (once)
  useEffect(() => {
    if (hasLoaded.current || !settings) return;
    hasLoaded.current = true;
    setValues(settingsToValues(settings));
    const rawSitelinks = (settings as any)?.sitelinks;
    if (Array.isArray(rawSitelinks)) setSitelinks(rawSitelinks);
  }, [settings]);

  // Always valid
  useEffect(() => {
    onValidChange(true);
  }, [onValidChange]);

  // Auto-save on changes
  useEffect(() => {
    if (!config) return;
    const timeout = setTimeout(() => {
      const mapped = valuesToSettings(values, config.toggles);
      onSave({
        ...mapped,
        sitelinks: sitelinks as any,
        step_enhancements_completed: true,
      } as any);
    }, 500);
    return () => clearTimeout(timeout);
  }, [values, sitelinks, config]);

  const handleChange = useCallback((key: string, value: boolean) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleBatchChange = useCallback((updates: Record<string, boolean>) => {
    setValues((prev) => ({ ...prev, ...updates }));
  }, []);

  // Check if any site links toggle is enabled
  const showSitelinks = useMemo(() => {
    // Check toggle_key values (from panel interactions)
    const toggleKeys = Object.entries(values).some(
      ([key, val]) => (key.includes("site_links") || key.includes("site_extensions")) && val,
    );
    return toggleKeys;
  }, [values]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Creative Enhancements</CardTitle>
          <CardDescription>
            Choose which Advantage+ creative enhancements to enable by default. These can be overridden per ad.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EnhancementTogglesPanel values={values} onChange={handleChange} onBatchChange={handleBatchChange} />
        </CardContent>
      </Card>

      {showSitelinks && (
        <SitelinksSection sitelinks={sitelinks} onChange={setSitelinks} />
      )}
    </div>
  );
}
